import {
  Box,
  Card,
  Grid,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Button from '@mui/material/Button';
import { Cancel } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import {
  DisplayMode,
  EntityTypes,
  Permit,
  PermitInviteBody,
  PermitType,
  Property,
  PropertyGroup,
  PhoneType,
} from 'types';
import {
  ChangeEvent,
  forwardRef,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import SendIcon from '@mui/icons-material/Send';

import { useTranslation } from 'react-i18next';
import { cardHeaderSx, cardSx, mobileCardSx } from 'utils/styleProps';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useFetcher } from 'react-router';
import { PermitTableActionHandler } from './PermitTable';
import AuturaTextField from 'components/inputs/AuturaTextField';
import AuturaDropdown from 'components/inputs/AuturaDropdown';
import { ROUTES } from 'router/routes';
import AuturaPhoneField from 'components/inputs/AuturaPhoneField';
import { TransitionProps } from '@mui/material/transitions';
import { getBlankPermitType } from 'utils/emptyEntities';
import useTenantID from 'hooks/useTenantID';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction='left' ref={ref} {...props} />;
});
interface PermitInviteDialogProps {
  isOpen: boolean;
  handleCancel: () => void;
  permitTypeOptions: PermitType[];
  entity: Property | PropertyGroup;
  displayMode: DisplayMode;
  fetcherKey: string;
  permit: Permit | null;
}

const PermitInviteDialog = ({
  isOpen,
  handleCancel,
  permitTypeOptions,
  entity,
  fetcherKey,
  displayMode,
  permit,
}: PermitInviteDialogProps): ReactNode => {
  const { t } = useTranslation();
  const tenantID = useTenantID();

  const { palette, breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('lg'));
  const fetcher = useFetcher({ key: fetcherKey });
  const [name, setName] = useState(permit?.contact?.name || '');
  const [email, setEmail] = useState(permit?.contact?.primaryEmail || '');
  const [phone, setPhone] = useState(permit?.contact?.primaryPhone || '');
  const [permitType, setPermitType] = useState<PermitType>(
    permitTypeOptions[0] ?? getBlankPermitType(tenantID),
  );
  const [inviteType, setInviteType] = useState<PermitTableActionHandler>(
    PermitTableActionHandler.UNKNOWN,
  );

  const [deliveryMethod, setDeliveryMethod] = useState<'SMS' | 'Email'>(
    'Email',
  );

  useEffect(() => {
    if (permitType.data.selfService) {
      setInviteType(PermitTableActionHandler.SEND_SELF_SERVICE_LINK);
    } else if (permit) {
      setInviteType(PermitTableActionHandler.RESEND_INVITE);
      setPermitType(permit.permitType);
    } else {
      setInviteType(PermitTableActionHandler.SEND_INVITE);
    }
  }, [permit]);

  const handleSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (inviteType === PermitTableActionHandler.RESEND_INVITE) {
      return;
    }

    const selectedPermitType = permitTypeOptions.find(
      (pt) => pt.name === e.target.value,
    );
    if (selectedPermitType) {
      setPermitType(selectedPermitType);
      if (selectedPermitType.data.selfService) {
        setInviteType(PermitTableActionHandler.SEND_SELF_SERVICE_LINK);
      } else {
        setInviteType(PermitTableActionHandler.SEND_INVITE);
      }
    }
  };
  const handleSendInvite = () => {
    const inviteBody: PermitInviteBody = {
      entityName: entity.name,
      name,
      email,
      phone,
      selfService: permitType.data.selfService,
      permitTypeID: permitType.id,
    };
    fetcher.submit(
      {
        body: JSON.stringify(inviteBody),
        handler: PermitTableActionHandler.SEND_INVITE,
        permitTypeID: permitType.id || 0,
        entityType:
          displayMode === 'property'
            ? EntityTypes.Property
            : EntityTypes.PropertyGroup,
        entityID: entity.id,
        tenantID: entity.tenantID,
        permitID: permit?.id || 0,
      },
      {
        method: 'POST',
        action: ROUTES.PROPERTY_PERMITS,
        encType: 'application/json',
      },
    );
  };
  const handleResendInvite = () => {
    const inviteBody: PermitInviteBody = {
      entityName: entity.name,
      name,
      email,
      phone,
      selfService: permitType.data.selfService,
      permitTypeID: permitType.id,
    };
    fetcher.submit(
      {
        body: JSON.stringify(inviteBody),
        handler: PermitTableActionHandler.RESEND_INVITE,
        permitTypeID: permitType.id || 0,
        entityType:
          displayMode === 'property'
            ? EntityTypes.Property
            : EntityTypes.PropertyGroup,
        entityID: entity.id,
        tenantID: entity.tenantID,
        permitID: permit?.id || 0,
      },
      {
        method: 'POST',
        action: ROUTES.PROPERTY_PERMITS,
        encType: 'application/json',
      },
    );
  };
  const handleSendSelfServiceLink = () => {
    const inviteBody: PermitInviteBody = {
      entityName: entity.name,
      name,
      email,
      phone,
      selfService: permitType.data.selfService,
      permitTypeID: permitType.id,
    };
    fetcher.submit(
      {
        body: JSON.stringify(inviteBody),
        handler: PermitTableActionHandler.SEND_SELF_SERVICE_LINK,
        permitTypeID: permitType.id || 0,
        entityType:
          displayMode === 'property'
            ? EntityTypes.Property
            : EntityTypes.PropertyGroup,
        entityID: entity.id,
        tenantID: entity.tenantID,
        permitID: permit?.id || 0,
      },
      {
        method: 'POST',
        action: ROUTES.PROPERTY_PERMITS,
        encType: 'application/json',
      },
    );
  };
  const handleSubmit = () => {
    if (inviteType === PermitTableActionHandler.SEND_INVITE) {
      handleSendInvite();
    } else if (inviteType === PermitTableActionHandler.RESEND_INVITE) {
      handleResendInvite();
    } else if (inviteType === PermitTableActionHandler.SEND_SELF_SERVICE_LINK) {
      handleSendSelfServiceLink();
    }
  };

  const title = useMemo((): string => {
    if (inviteType === PermitTableActionHandler.SEND_INVITE) {
      return t('dialogs.invite.send_invite_title', { ns: 'permits' });
    } else if (inviteType === PermitTableActionHandler.RESEND_INVITE) {
      return t('dialogs.invite.resend_invite_title', { ns: 'permits' });
    } else if (inviteType === PermitTableActionHandler.SEND_SELF_SERVICE_LINK) {
      return t('dialogs.invite.send_self_service_link_title', {
        ns: 'permits',
      });
    } else {
      return '';
    }
  }, [inviteType]);

  const sendButtonText = useMemo((): string => {
    if (inviteType === PermitTableActionHandler.SEND_INVITE) {
      return t('buttons.send_invite', { ns: 'permits' });
    } else if (inviteType === PermitTableActionHandler.RESEND_INVITE) {
      return t('buttons.resend_invite', { ns: 'permits' });
    } else if (inviteType === PermitTableActionHandler.SEND_SELF_SERVICE_LINK) {
      return t('buttons.send_self_service_link', { ns: 'permits' });
    } else {
      return '';
    }
  }, [inviteType]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      aria-label={`${t('permit.invite.header')} Dialog`}
      TransitionComponent={Transition}
      fullScreen={isMobile}
      sx={{ mt: { xs: -1, lg: 0 }, pb: 0 }}
    >
      <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
        <Card sx={isMobile ? mobileCardSx : cardSx}>
          <Box
            sx={
              isMobile
                ? {
                    display: 'flex',
                    p: { xs: 2, sm: 4 },
                    backgroundColor: palette.secondary.main,
                  }
                : cardHeaderSx
            }
          >
            <Box sx={{ flex: 1, p: 0.5 }}>
              <Typography
                variant={'h4'}
                sx={{
                  color: palette.secondary.contrastText,
                  textAlign: 'left',
                }}
              >
                {title}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            <Grid container width={'100%'} spacing={1.5}>
              <Grid size={12}>
                <AuturaDropdown
                  name='permit.permitType'
                  label={t('permit_type.label', { ns: 'entities' })}
                  value={permitType.name}
                  setValue={handleSelect}
                  options={permitTypeOptions.map((pt) => pt.name)}
                  required
                  disabled={
                    inviteType === PermitTableActionHandler.RESEND_INVITE
                  }
                />
              </Grid>
              <Grid size={12}>
                <AuturaTextField
                  label={t('invite.name', { ns: 'permits' })}
                  required
                  name={'permit.invite.name'}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaDropdown
                  name='permit.invite.deliveryMethod'
                  label={t('invite.deliveryMethod', { ns: 'permits' })}
                  initialValue='email'
                  value={deliveryMethod}
                  setValue={(e) =>
                    setDeliveryMethod(e.target.value as 'SMS' | 'Email')
                  }
                  options={['Email', 'SMS']}
                  required
                />
              </Grid>
              {deliveryMethod === 'Email' && (
                <Grid
                  size={{
                    xs: 12,
                    lg: 6,
                  }}
                >
                  <AuturaTextField
                    label={t('invite.email', { ns: 'permits' })}
                    name={'permit.invite.email'}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    validators={['isEmail']}
                    errorMessages={['Invalid email']}
                    required
                  />
                </Grid>
              )}
              {deliveryMethod === 'SMS' && (
                <Grid
                  size={{
                    xs: 12,
                    lg: 6,
                  }}
                >
                  <AuturaPhoneField
                    name={'permit.invite.phone'}
                    label={t('invite.phone', { ns: 'permits' })}
                    typeFieldName={'permit.invite.phoneType'}
                    typeFieldLabel='Type'
                    phoneNumber={phone}
                    setPhoneNumber={setPhone}
                    phoneType={PhoneType.Mobile}
                    setPhoneType={() => {}} // Disabled - always mobile for SMS
                    typeFieldReadonly={true}
                    required
                  />
                </Grid>
              )}
              <Grid size={12}>
                <Stack
                  width='100%'
                  direction='row'
                  spacing={2}
                  justifyContent={{ xs: 'space-between', lg: 'flex-end' }}
                  mt={2}
                  display='flex'
                  alignItems='flex-end'
                  alignContent='flex-end'
                >
                  <Button
                    variant={'outlined'}
                    onClick={handleCancel}
                    color={'secondary'}
                    endIcon={<Cancel />}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    variant={'contained'}
                    type='submit'
                    color={'secondary'}
                    endIcon={<SendIcon />}
                  >
                    {sendButtonText}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </ValidatorForm>
    </Dialog>
  );
};
export default PermitInviteDialog;
